import { getConfig, isInitialized } from '../core/config';
import { post } from '../core/client';
import type { AffiliateValidation, SDKResponse } from '../types';

const STORAGE_KEY = 'shoppex:affiliate_code:v1';
const SESSION_STORAGE_KEY = 'shoppex:affiliate_session:v1';
const DEFAULT_TTL_DAYS = 30;
// 24 base-36 characters provide about 124 bits of entropy and stay within the API's 8-64 character limit.
const FALLBACK_SESSION_KEY_LENGTH = 24;
const SESSION_KEY_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

export type AffiliateAttributionMode = 'LAST_CLICK' | 'FIRST_CLICK';

type StoredAffiliate = {
  code: string;
  expiresAt: number;
  /**
   * The attribution mode that applied when this entry was written (ADR-0067).
   *
   * Optional on purpose. The storage key stays `v1` because bumping it would
   * discard every in-flight attribution in every buyer's browser — real,
   * already-earned commission. An entry written before this field existed
   * reads as LAST_CLICK, which is exactly the behaviour it was written under.
   */
  mode?: AffiliateAttributionMode;
  /**
   * The window this entry was written under, in days. Same optionality rule as
   * `mode`: absent on pre-ADR-0067 entries, which then fall back to 30.
   *
   * Kept so a re-landing that cannot reach the server can renew the entry with
   * the shop's window instead of collapsing it to the 30-day default.
   */
  windowDays?: number;
};

function normalizeMode(value: unknown): AffiliateAttributionMode {
  return value === 'FIRST_CLICK' ? 'FIRST_CLICK' : 'LAST_CLICK';
}

function nowMs() {
  return Date.now();
}

function ttlMs(days: number) {
  return Math.max(1, days) * 24 * 60 * 60 * 1000;
}

function normalizeAffiliateCode(code: string | null | undefined): string | null {
  const normalized = code?.trim().toLowerCase();
  return normalized ? normalized : null;
}

function safeRead(): StoredAffiliate | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAffiliate;
    if (!parsed || typeof parsed.code !== 'string' || typeof parsed.expiresAt !== 'number') return null;
    return parsed.mode === undefined ? parsed : { ...parsed, mode: normalizeMode(parsed.mode) };
  } catch {
    return null;
  }
}

function safeWrite(value: StoredAffiliate) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

/**
 * Write a previously read entry back verbatim (ADR-0067).
 *
 * Distinct from `setAffiliateCode`, which recomputes `expiresAt` from now:
 * restoring a first-click hold must not extend its window, because the window
 * runs from the click that earned it.
 */
function restoreStoredAffiliate(value: StoredAffiliate): void {
  safeWrite(value);
}

export function setAffiliateCode(
  code: string | null | undefined,
  ttlDays = DEFAULT_TTL_DAYS,
  mode?: AffiliateAttributionMode,
): string | null {
  const normalized = normalizeAffiliateCode(code);
  if (!normalized) {
    clearAffiliateCode();
    return null;
  }

  safeWrite({
    code: normalized,
    expiresAt: nowMs() + ttlMs(ttlDays),
    windowDays: ttlDays,
    ...(mode ? { mode } : {}),
  });
  return normalized;
}

/**
 * The stored attribution, or null when nothing valid is held.
 *
 * Unlike `getAffiliateCode()` this also reports the mode the entry was written
 * under, which is what `captureAffiliateFromUrl` needs BEFORE it decides
 * whether it may overwrite.
 */
export function getStoredAffiliate(): {
  code: string;
  mode: AffiliateAttributionMode;
  windowDays: number;
  /**
   * When this entry expires, as written. Reported so a hold that has to be put
   * back after an early overwrite keeps its original expiry — the window runs
   * from the click that earned it, not from the landing that failed to take it.
   */
  expiresAt: number;
} | null {
  const stored = safeRead();
  if (!stored) return null;
  if (stored.expiresAt <= nowMs()) {
    clearAffiliateCode();
    return null;
  }

  const code = normalizeAffiliateCode(stored.code);
  if (!code) return null;

  const windowDays = typeof stored.windowDays === 'number' && stored.windowDays > 0
    ? stored.windowDays
    : DEFAULT_TTL_DAYS;

  return { code, mode: normalizeMode(stored.mode), windowDays, expiresAt: stored.expiresAt };
}

export function clearAffiliateCode(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getAffiliateCode(): string | null {
  const stored = safeRead();
  if (!stored) return null;
  if (stored.expiresAt <= nowMs()) {
    clearAffiliateCode();
    return null;
  }
  return normalizeAffiliateCode(stored.code);
}

function createAffiliateSessionKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to the browser-safe random key.
  }

  let key = '';
  for (let index = 0; index < FALLBACK_SESSION_KEY_LENGTH; index += 1) {
    key += SESSION_KEY_ALPHABET[Math.floor(Math.random() * SESSION_KEY_ALPHABET.length)];
  }
  return key;
}

// Retained for the page's lifetime when sessionStorage is unavailable, so
// repeated events in a storage-restricted browser still share one key and the
// server-side session dedupe keeps working.
let inMemorySessionKey: string | null = null;

function getAffiliateSessionKey(): string {
  try {
    const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored && stored.length >= 8 && stored.length <= 64) {
      return stored;
    }
  } catch {
    // Read blocked entirely: the retained key is all we have.
    if (inMemorySessionKey) return inMemorySessionKey;
  }

  const key = createAffiliateSessionKey();
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, key);
    // A write that silently no-ops (quota) must not hand out a fresh key per
    // event — verify it landed before trusting storage over the retained key.
    if (window.sessionStorage.getItem(SESSION_STORAGE_KEY) === key) {
      inMemorySessionKey = key;
      return key;
    }
  } catch {
    // Fall through to the retained key.
  }

  if (!inMemorySessionKey) inMemorySessionKey = key;
  return inMemorySessionKey;
}

export async function trackAffiliateEvent(
  eventType: 'add_to_cart' | 'checkout_started',
  options?: {
    /**
     * The code the surrounding call actually resolved and submitted (e.g.
     * checkout()'s tri-state result). `null` means the caller explicitly
     * submitted WITHOUT a referral — no event is recorded, so an ambient
     * stored code is never credited for a sale it did not get. Omit the
     * options object entirely to attribute to the stored ambient code.
     */
    code: string | null;
    /**
     * Overrides the per-browser-session dedupe key. checkout entry points
     * pass `inv:<invoiceId>` so every created invoice counts as exactly one
     * checkout — a session-wide key would drop the second checkout of a
     * buyer who orders twice in one session while both sales still count.
     */
    dedupeKey?: string;
  }
): Promise<void> {
  try {
    if (typeof window === 'undefined' || !isInitialized()) return;

    const code = options === undefined
      ? getAffiliateCode()
      : normalizeAffiliateCode(options.code);
    if (!code) return;

    const config = getConfig();
    // Raw fetch instead of the shared post helper: `keepalive` lets the
    // request survive the checkout redirect that immediately follows, which
    // would otherwise cancel it and undercount checkouts.
    await fetch(`${config.apiBaseUrl}/v1/storefront/affiliates/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        shop_slug: config.storeSlug,
        code,
        event_type: eventType,
        // Dedupe key: per browser session by default (one add-to-cart per
        // session per link), or the caller's key — checkout passes one per
        // created invoice.
        session_key: options?.dedupeKey?.slice(0, 64) ?? getAffiliateSessionKey(),
      }),
    });
  } catch {
    // Funnel tracking must never break cart or checkout behavior.
  }
}

export async function validateAffiliateCode(code: string): Promise<SDKResponse<AffiliateValidation>> {
  const normalizedCode = normalizeAffiliateCode(code);
  if (!normalizedCode) {
    return {
      success: false,
      data: {
        valid: false,
        affiliate_code: null,
        discount_active: false,
        discount_percent: 0,
      },
      message: 'Affiliate code is required',
    };
  }

  const config = getConfig();
  // The attribution the browser already holds, so the response can report ITS
  // policy too (ADR-0067). `applyAffiliateCode` below needs that to know
  // whether storing this code would take a sale away from a protected
  // first-click hold.
  const held = getStoredAffiliate();
  const response = await post<AffiliateValidation>(
    '/v1/storefront/affiliates/resolve',
    {
      shop_slug: config.storeSlug,
      code: normalizedCode,
      ...(held && held.code !== normalizedCode ? { held_code: held.code } : {}),
    },
    { retries: 0 }
  );

  if (!response.success) {
    return response;
  }

  if (!response.data?.valid || !response.data.affiliate_code) {
    const programDisabled = response.data?.program_enabled === false;
    return {
      success: false,
      data: {
        valid: false,
        ...(response.data?.program_enabled !== undefined ? { program_enabled: response.data.program_enabled } : {}),
        affiliate_code: null,
        discount_active: false,
        discount_percent: 0,
      },
      message: response.message
        ?? (programDisabled ? 'Affiliate program is disabled for this shop.' : 'Invalid affiliate code.'),
    };
  }

  return {
    success: true,
    data: {
      valid: true,
      ...(response.data.program_enabled !== undefined ? { program_enabled: response.data.program_enabled } : {}),
      affiliate_code: normalizeAffiliateCode(response.data.affiliate_code),
      discount_active: Boolean(response.data.discount_active),
      discount_percent: Number(response.data.discount_percent ?? 0),
      // Passed through rather than dropped: a caller that stores the code has
      // no other way to learn the policy, and defaulting to 30-day last-click
      // would quietly discard a first-click tier and any custom window.
      attribution_mode: normalizeMode(response.data.attribution_mode),
      attribution_window_days: typeof response.data.attribution_window_days === 'number'
        && response.data.attribution_window_days >= 1
        ? Math.trunc(response.data.attribution_window_days)
        : DEFAULT_TTL_DAYS,
      held_attribution_mode: response.data.held_attribution_mode === 'FIRST_CLICK'
        || response.data.held_attribution_mode === 'LAST_CLICK'
        ? response.data.held_attribution_mode
        : null,
    },
    ...(response.message ? { message: response.message } : {}),
  };
}

/**
 * Validate a manually entered code and store it (ADR-0067).
 *
 * Attribution is governed by the same rules as a `?ref=` landing, deliberately:
 * a themed "apply code" box must not be a way around first-click, or the rule
 * is bypassed by any storefront that offers one. So a still-protected hold
 * keeps the sale, and the stored entry always carries the mode and window the
 * server just reported — the old unconditional `setAffiliateCode(code)` wrote
 * a 30-day last-click entry whatever the shop or tier had configured.
 *
 * The result reports what is now ATTRIBUTED, not merely what was validated:
 * `affiliate_code` carries the winning code and `applied` says whether that is
 * the one you passed. The documented integration hands `affiliate_code`
 * straight to `checkout({ affiliateCode })`, and an explicit checkout code
 * overrides storage — so returning the losing code here would credit it and
 * walk right around the hold this function just honoured.
 */
export async function applyAffiliateCode(code: string): Promise<SDKResponse<AffiliateValidation>> {
  const held = getStoredAffiliate();
  const result = await validateAffiliateCode(code);

  if (result.success && result.data?.affiliate_code) {
    const applied = result.data.affiliate_code;
    const holdsOtherCode = held !== null && held.code !== applied;
    // The server's answer about the HELD code decides, never the arriving
    // one's; null means it no longer resolves, and the stored mode is then all
    // that is left.
    const heldMode = holdsOtherCode
      ? (result.data.held_attribution_mode ?? held.mode)
      : null;

    if (heldMode === 'FIRST_CLICK' && held) {
      return {
        ...result,
        data: { ...result.data, affiliate_code: held.code, applied: false },
      };
    }

    setAffiliateCode(
      applied,
      result.data.attribution_window_days ?? DEFAULT_TTL_DAYS,
      result.data.attribution_mode,
    );
    return { ...result, data: { ...result.data, applied: true } };
  }

  return result;
}

/**
 * Capture an affiliate code from the current URL and store it (ADR-0067).
 *
 * Example:
 * - URL: https://mystore.com/product/abc?ref=deadbeef
 * - captureAffiliateFromUrl() stores "deadbeef" and returns it.
 *
 * Under LAST_CLICK (the default, and the only behaviour before ADR-0067) a new
 * `?ref=` replaces whatever was stored. Under FIRST_CLICK an existing,
 * unexpired attribution wins and the new code is NOT stored — the landing is
 * still reported to the server so the affiliate's click statistics stay
 * honest, but the credit stays with whoever was there first.
 *
 * The mode comes from the server, which means the FIRST landing in a browser
 * can never be blocked by it — correct, because there is nothing to protect
 * yet. From then on the mode travels with the stored entry.
 *
 * Enforcement is client-side. A buyer who edits localStorage can re-attribute
 * their own visit; this is the same trust level as the window itself and must
 * not be presented to merchants as fraud protection.
 */
export async function captureAffiliateFromUrl(param = 'ref'): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  let code: string | null = null;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(param);
    code = raw ? raw.trim() : null;
  } catch {
    code = null;
  }

  code = normalizeAffiliateCode(code);
  if (!code) return null;

  // Read BEFORE writing: under first-click the held entry is the one that
  // counts, and the old "store immediately" would have destroyed it.
  const held = getStoredAffiliate();
  // A DIFFERENT attribution is held, so this landing can take a sale away from
  // someone. Whether it may is the server's answer below.
  const holdsOtherCode = held !== null && held.code !== code;
  // The stored mode can be stale. Preserve every different held code until
  // the server resolves its policy, including while checkout reads storage.
  if (!holdsOtherCode) {
    // Store immediately so we don't lose it if the attribution call fails.
    // The window is the previous one we were told about, not a fixed 30 days —
    // a shop with a 90-day window must not silently drop to 30 when the
    // attribution call fails and this early write is all that survives.
    setAffiliateCode(code, held?.windowDays ?? DEFAULT_TTL_DAYS, held?.mode);
  }

  // Optional: validate + normalize with backend. If invalid, clear it.
  if (isInitialized()) {
    try {
      const config = getConfig();
      const res = await post<{
        accepted?: boolean;
        affiliate_code?: string | null;
        attribution_mode?: string | null;
        attribution_window_days?: number | null;
        held_attribution_mode?: string | null;
      }>(
        '/v1/storefront/affiliates/attribution',
        {
          shop_slug: config.storeSlug,
          code,
          // Sent so the server can report the policy of the attribution we
          // already hold. Its mode — not the arriving code's — decides whether
          // that hold may be released.
          ...(held ? { held_code: held.code } : {}),
        },
        { retries: 0 }
      );

      const serverMode = res.success && res.data?.attribution_mode
        ? normalizeMode(res.data.attribution_mode)
        : null;
      const serverWindow = res.success && typeof res.data?.attribution_window_days === 'number'
        ? res.data.attribution_window_days
        : DEFAULT_TTL_DAYS;

      // The shop may have switched to last-click since the entry was written, and the
      // server is the authority — without this check a stale FIRST_CLICK entry
      // would keep winning for up to a full window (365 days) and pay the
      // wrong affiliate on every sale in between.
      // Whether the hold survives is decided by the HELD code's current
      // policy, never by the arriving one. Affiliate A stored under a
      // first-click tier must keep the sale even when affiliate B arrives from
      // a last-click tier — using B's mode here would hand B the commission.
      // `held_attribution_mode` is null when the server no longer resolves the
      // held code; the stored mode is then the only information left.
      const heldMode = res.success && res.data?.held_attribution_mode
        ? normalizeMode(res.data.held_attribution_mode)
        : null;
      const holdsFirstClick = holdsOtherCode && (heldMode ?? held?.mode) === 'FIRST_CLICK';

      if (holdsFirstClick && held) {
        // First-click still applies: keep the first attribution. Its expiry is
        // deliberately NOT extended — the window runs from the click that
        // earned it.
        restoreStoredAffiliate({
          code: held.code,
          expiresAt: held.expiresAt,
          windowDays: held.windowDays,
          mode: heldMode ?? held.mode,
        });
        return held.code;
      }

      if (res.success && res.data?.accepted && res.data.affiliate_code) {
        setAffiliateCode(res.data.affiliate_code, serverWindow, serverMode ?? undefined);
        return res.data.affiliate_code;
      }
      // Clear only on a definitive refusal (the server answered and said the
      // code is not attributable). A rate limit or transport failure must not
      // destroy a valid referral before checkout — from-cart validates later.
      if (res.success && res.data && res.data.accepted === false) {
        if (holdsOtherCode) return held.code;
        clearAffiliateCode();
        return null;
      }
      // Reached on a non-success response (`post()` resolves rather than
      // throws on an HTTP failure, e.g. the route's 429). Nothing was
      // overwritten in that case, so returning the arriving code would hand a
      // custom storefront that submits this value manually to the newcomer
      // while storage still holds the first click. Same answer as the catch
      // below, for the same reason.
      return holdsOtherCode ? held.code : code;
    } catch {
      // Without a policy response, preserve the existing attribution.
      return holdsOtherCode ? held.code : code;
    }
  }

  // Not initialized: preserve any different held attribution.
  return holdsOtherCode ? held.code : code;
}
